'use client'

import type { WalletGroupedAccounts } from '@polaris/core/models'
import { useAtomValue, useSetAtom } from 'jotai'
import { Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import {
  pendingAccountDialogAtom,
  usePendingAccountActions,
  usePendingAccountDialog,
  walletConnectionQueueAtom,
} from '@/src/stores/accounts/detection'
import { pendingAccountsAtom } from '@/src/stores/accounts/wallets'
import { selectWalletsDialogOpenAtom } from '@/src/stores/select-wallets'
import { cn } from '@/src/utils/styles'
import { Button } from '../ui/button'
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog'
import { AccountCard } from './account-card'

interface AddPendingAccountProps {
  titleCopy?: string
  className?: string
  account: WalletGroupedAccounts
  onAdd: () => void
  onCancel: () => void
  isAddingAccount?: boolean
}

export function AddPendingAccount({
  titleCopy,
  className,
  account,
  onAdd,
  onCancel,
  isAddingAccount = false,
}: AddPendingAccountProps) {
  const [isLoading, setIsLoading] = useState(true)
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false)
    }, 1_000)

    return () => clearTimeout(timer)
  }, [])

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-8 justify-center py-8 animate-in fade-in duration-300',
        className,
      )}
    >
      <div className='flex flex-col gap-3'>
        <span className='text-4xl font-serif font-extralight text-primary'>
          {titleCopy ?? 'New wallet detected'}
        </span>
        <span className='text-secondary'>Would you like to add it?</span>
      </div>
      <AccountCard
        account={account}
        isLoading={isLoading}
        className='min-w-[248px]'
      />
      <span className='text-tertiary text-sm text-center w-full'>
        This action doesn&rsquo;t import your account or grant access to its
        funds without the wallet connected.
      </span>
      <div className='flex w-full gap-3'>
        <Button
          className='flex-1'
          variant='secondary'
          size='lg'
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          className='flex-1'
          variant='primary'
          size='lg'
          onClick={onAdd}
          disabled={isAddingAccount}
        >
          Add wallet
        </Button>
      </div>
    </div>
  )
}

interface WalletAddedSuccessProps {
  className?: string
  successAccount: WalletGroupedAccounts
  onClose?: () => void
}

export function WalletAddedSuccess({
  className,
  successAccount,
  onClose,
}: WalletAddedSuccessProps) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose?.()
    }, 500)

    return () => clearTimeout(timer)
  }, [onClose])

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-8 pt-8 pb-36 justify-center animate-in fade-in duration-300',
        className,
      )}
    >
      <div className='flex flex-col items-center gap-3'>
        <div className='flex items-center justify-center size-8 bg-green-400 rounded-full'>
          <Check className='size-6 text-green-800' />
        </div>
      </div>
      <AccountCard
        account={successAccount}
        totalValueReplaceContent='Connected'
        size='lg'
        className='w-[292px] h-full'
      />
    </div>
  )
}

export function DetectedPendingAccountDialog({
  className,
}: {
  className?: string
}) {
  const { groupedAccounts: pendingGroupedAccounts } =
    useAtomValue(pendingAccountsAtom)
  const setPendingAccountDialog = usePendingAccountDialog()
  const pendingAccountGroupingKey = useAtomValue(pendingAccountDialogAtom)
  const { addPendingAccount, isAddingAccount } = usePendingAccountActions()
  const [showSuccess, setShowSuccess] = useState<WalletGroupedAccounts | null>(
    null,
  )
  const walletDialogAlreadyOpen = useAtomValue(selectWalletsDialogOpenAtom)
  const setWalletConnectionQueue = useSetAtom(walletConnectionQueueAtom)

  // Find the specific pending account using the grouping key from the atom
  const account = pendingAccountGroupingKey
    ? pendingGroupedAccounts.find(
        (acc) => acc.grouping_key === pendingAccountGroupingKey,
      )
    : undefined

  const handleAdd = async () => {
    if (!account) return

    await addPendingAccount(account)
    setShowSuccess(account)
  }

  const handleDone = () => {
    // Reset to initial state when dialog is closed/done
    setShowSuccess(null)
    setPendingAccountDialog(null)

    if (!account) return
  }

  return (
    <Dialog
      open={Boolean(account || showSuccess) && !walletDialogAlreadyOpen}
      onOpenChange={(open) => {
        if (!open) {
          if (showSuccess) {
            handleDone()
          }
          setWalletConnectionQueue(new Set())
        }
      }}
    >
      <DialogContent
        className={cn('text-center h-fit max-w-[28rem]', {
          'bg-green-800': showSuccess,
        })}
        showCloseButton
        showBackButton={false}
        closeAction={() => {
          setPendingAccountDialog(null)
          setWalletConnectionQueue(new Set())
        }}
      >
        <DialogTitle />

        {showSuccess ? (
          <WalletAddedSuccess
            successAccount={showSuccess}
            className={cn(className, 'p-8 pt-0 pb-12')}
            onClose={handleDone}
          />
        ) : account ? (
          <AddPendingAccount
            titleCopy='New wallet detected'
            account={account}
            onAdd={handleAdd}
            onCancel={() => setPendingAccountDialog(null)}
            isAddingAccount={isAddingAccount}
            className={cn(className, 'mt-0 p-8 pt-0')}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
